import type { AttachmentDetails } from 'api';
import { Stack } from 'ui';

import { formatDirtScore } from '../../utils/formatDirtScore/formatDirtScore';
import { formatPercentageFeature } from '../../utils/formatFeature/formatFeature';
import { MaterialBreakdownItem } from '../MaterialBreakdownItem/MaterialBreakdownItem';

type Props = {
  targetWeightPercentage: number;
  attachmentDetails: AttachmentDetails;
  showAerosol?: boolean;
};

export const AttachmentDetailsBreakdown = ({ attachmentDetails, targetWeightPercentage, showAerosol }: Props) => {
  const totalAluminiumPercentage =
    targetWeightPercentage +
    attachmentDetails.foil_and_tray +
    attachmentDetails.food_can +
    attachmentDetails.other_aluminium;
  const totalNonAluminiumPercentage = 1 - totalAluminiumPercentage;
  const aerosolLabel = showAerosol ? 'Aluminium Aerosol' : 'Other Aluminium';

  return (
    <Stack gap={2} data-testid="attachment-details-breakdown">
      <div>
        <MaterialBreakdownItem
          label="Aluminium"
          value={formatPercentageFeature(totalAluminiumPercentage)}
          variant="bold"
          data-testid="material-breakdown-aluminium"
        />
        <MaterialBreakdownItem
          label="UBC"
          value={formatPercentageFeature(targetWeightPercentage)}
          data-testid="material-breakdown-ubc"
        />
        <MaterialBreakdownItem
          label="Foil and Tray"
          value={formatPercentageFeature(attachmentDetails.foil_and_tray)}
          data-testid="material-breakdown-foil-and-tray"
        />
        <MaterialBreakdownItem
          label="Aluminium Food Can"
          value={formatPercentageFeature(attachmentDetails.food_can)}
          data-testid="material-breakdown-aluminium-food-can"
        />
        <MaterialBreakdownItem
          label={aerosolLabel}
          value={formatPercentageFeature(attachmentDetails.other_aluminium)}
          data-testid="material-breakdown-other-aluminium"
        />
      </div>

      <div>
        <MaterialBreakdownItem
          label="Non-Aluminium"
          value={formatPercentageFeature(totalNonAluminiumPercentage)}
          variant="bold"
          data-testid="material-breakdown-non-aluminium"
        />
        <MaterialBreakdownItem
          label="Other Metal"
          value={formatPercentageFeature(attachmentDetails.other_metal)}
          data-testid="material-breakdown-other-metal"
        />
        <MaterialBreakdownItem
          label="Non-Metal"
          value={formatPercentageFeature(attachmentDetails.non_metal)}
          data-testid="material-breakdown-non-metal"
        />
      </div>
      <div>
        <MaterialBreakdownItem
          variant="bold"
          label="Cleanliness"
          tooltipContent="Cleanliness is a measure of how clean the material is. This ranges from very clean, clean, average, dirty and very dirty. Dirt includes grease, spots and dust present on the surface of the material."
          value={formatDirtScore(attachmentDetails.dirt_score)}
        />
      </div>
    </Stack>
  );
};
